import { isStagePlace, type PlaceId } from '../ids';
import { SEATS } from './seats';

// 영상·음성으로 이어지는 모임의 규칙 (사용자가 정했다, docs/architecture.md 6.1·8.1). 서버(신호 전달·SFU 입장 검사)와 화면(연결 대상)이 같은 함수를 쓴다.
// 모임 키: 같은 키를 가진 사람끼리 이어진다. null이면 아무와도 이어지지 않는다.
//   학교      앉았든 서 있든 학교 안 모든 사람이 한 모임                        → `room:school`
//   테이블·벤치 앉으면 같은 테이블(스타박스 둘, 퍼스트파이브 최대 여섯, 광장 벤치 둘)만. 앉은 사람은 지나가는 사람과 이어지지 않는다 → `table:<번호>`
//   그 밖에 서 있는 사람(마을·퍼스트파이브·스타박스 바닥)  같은 장소에서 가까운 사람끼리 → `near:<장소>`. 거리는 아래 canLink
//   모험 스테이지(`stage:<아이디>`)에 서 있거나 달리는 사람: 같은 스테이지 안에서 가까운 사람끼리 → `near:stage:<아이디>`. 스테이지마다 모임이 따로다
//   장소 밖(지도·제목)                                                         → null
// 가까운 사람끼리 이어지는 규칙은 v2와 같다: 쌍마다 계산하고(이어짐은 이어서 건너지 않는다: 가-나, 나-다가 이어져도 가-다는 아니다),
// 이어지기 시작하는 거리 NEAR_PX보다 이미 이어진 쌍은 FAR_PX까지 멀어져도 유지한다(경계에서 깜빡이지 않게). 거리는 같은 장소 안의 가로(x) 거리다. 스테이지는 세로로 긴 곳(2-3·3-3·4-3: 26×52~64칸)이 있어 x·y 직선거리다(숫자는 그대로).
export type LinkGroup = string | null;
export const SCHOOL_GROUP: LinkGroup = 'room:school';
export const NEAR_PX = 48, FAR_PX = 64;       // v2의 3칸·4칸(16px 칸)
export const SERVER_SLACK_PX = 24;            // 서버는 위치를 최대 한 번 늦게 알고 있다: 신호는 FAR_PX에 이만큼 더한 거리까지 전한다
export const NEAR_MAX = 6;                    // 가까운 사람과 동시에 이어지는 수(가까운 순)

export function linkGroup(room: PlaceId | null, seat: number): LinkGroup {
  if (room === null) return null;
  if (room === 'school') return SCHOOL_GROUP;
  const s = seat >= 0 ? SEATS[seat] : undefined;
  return s && s.room === room ? `table:${s.table}` : `near:${room}`;   // 지금 있는 장소의 자리에 앉아 있으면 그 테이블, 아니면 가까운 사람
}
// 가까운 사람 모임인가(그 밖의 키는 모두 「모임」: 테이블·학교). 화면 공유·손들기는 모임에서만 뜻이 있다
export const isNear = (g: LinkGroup) => g !== null && g.startsWith('near:');
export const isMeeting = (g: LinkGroup) => g !== null && !isNear(g);

// 두 사람이 이어지는가. wasLinked = 지금 이미 이어져 있다(멀어질 때 FAR_PX까지 유지). slack = 위치가 낡았을 수 있는 만큼 더하는 거리
export type Located = { room: PlaceId | null; seat: number; x: number; y?: number };
// 이어지는 거리 재기: 마을·건물은 옆에서 본 가로(x) 거리, 스테이지는 x·y 직선거리
export const linkDist = (a: Located, b: Located) => (isStagePlace(a.room) ? Math.hypot(a.x - b.x, (a.y ?? 0) - (b.y ?? 0)) : Math.abs(a.x - b.x));
export function canLink(a: Located, b: Located, wasLinked = false, slack = 0): boolean {
  const g = linkGroup(a.room, a.seat);
  if (g === null || g !== linkGroup(b.room, b.seat)) return false;
  return !isNear(g) || linkDist(a, b) <= (wasLinked ? FAR_PX : NEAR_PX) + slack;
}
